import {
  Button,
  Checkbox,
  type CSSVariablesResolver,
  colorsTuple,
  createTheme,
  Drawer,
  type MantineColorsTuple,
  Modal,
  mergeThemeOverrides,
  Radio,
  Switch,
} from '@mantine/core';
import classes from './theme.module.css';

// Design tokens: the core palette first, then the extra values the screens use,
// each with a dark counterpart in the same role.
const tokens = {
  light: {
    '--fs-ground': '#F7F5EF',
    '--fs-surface': '#FFFFFF',
    '--fs-sidebar': '#F0ECE3',
    '--fs-ink': '#1D1A14',
    '--fs-sub': '#5E574A',
    '--fs-border': '#E3DDD0',
    '--fs-line': '#EEE9DF',
    '--fs-hover': '#F3EFE6',
    '--fs-brand': '#FFB800',
    '--fs-brand-border': '#E0A200',
    '--fs-link': '#2B5BA8',
    '--fs-link-tint': '#E8EFFA',
    '--fs-risk-text': '#A3321A',
    '--fs-risk-bg': '#FCF1EC',
    '--fs-risk-border': '#E8BCAD',
    '--fs-ok-bg': '#E3F0EA',
    '--fs-ok-text': '#1C5A47',
    '--fs-pending-bg': '#FFF1CC',
    '--fs-pending-text': '#6B4A00',
    '--fs-tag-bg': '#EEE9DE',
    '--fs-tag-text': '#3F392F',
    '--fs-switch-on': '#2A2620',
    '--fs-code-bg': '#24211B',
    '--fs-code-text': '#F3EEE3',

    '--fs-brand-hover': '#F2AE00',
    '--fs-link-hover': '#1C3F78',
    '--fs-control-border': '#D6CEBD',
    '--fs-input-border': '#CFC7B6',
    '--fs-muted-bg': '#FAF8F3',
    '--fs-row-hover': '#FCFBF7',
    '--fs-selected-bg': '#FFF8E3',
    '--fs-nav-hover': '#E6E0D3',
    '--fs-nav-current-ring': '#DDD5C5',
    '--fs-nav-head': '#6A6253',
    '--fs-count-bg': '#E3DCCD',
    '--fs-count-text': '#3F392F',
    '--fs-switch-off': '#C9C1B0',
    '--fs-switch-thumb': '#FFFFFF',
    '--fs-switch-thumb-on': '#FFFFFF',
    '--fs-risk-tag-bg': '#FBE7E0',
    '--fs-risk-tag-text': '#8A2914',
    '--fs-risk-line': '#F0D3C8',
    '--fs-risk-heading': '#7E2611',
    '--fs-danger-border': '#E6BFB2',
    '--fs-danger-hover': '#FBEDE8',
    '--fs-enc-text': '#1C3F78',
    '--fs-bar-track': '#EEE9DE',
    '--fs-bar-fill': '#2A2620',
    '--fs-toast-bg': '#24211B',
    '--fs-toast-text': '#F7F3EA',
    '--fs-toast-border': '#5A5346',
    '--fs-toast-action': '#FFD666',
  },
  dark: {
    '--fs-ground': '#15130F',
    '--fs-surface': '#1E1B16',
    '--fs-sidebar': '#1A1713',
    '--fs-ink': '#F3EEE3',
    '--fs-sub': '#A89F8F',
    '--fs-border': '#332E26',
    '--fs-line': '#2A2621',
    '--fs-hover': '#272320',
    '--fs-brand': '#FFB800',
    '--fs-brand-border': '#E0A200',
    '--fs-link': '#8DB4F0',
    '--fs-link-tint': '#1E2A3F',
    '--fs-risk-text': '#F0937A',
    '--fs-risk-bg': '#3A1D15',
    '--fs-risk-border': '#6B3022',
    '--fs-ok-bg': '#16352B',
    '--fs-ok-text': '#9BD8C0',
    '--fs-pending-bg': '#3D3010',
    '--fs-pending-text': '#FFD666',
    '--fs-tag-bg': '#2E2A24',
    '--fs-tag-text': '#D8D0C2',
    '--fs-switch-on': '#F3EEE3',
    '--fs-code-bg': '#0F0E0B',
    '--fs-code-text': '#F3EEE3',

    '--fs-brand-hover': '#F2AE00',
    '--fs-link-hover': '#B5CEF5',
    '--fs-control-border': '#3D372D',
    '--fs-input-border': '#433C31',
    '--fs-muted-bg': '#1A1813',
    '--fs-row-hover': '#221F1A',
    '--fs-selected-bg': '#2A2414',
    '--fs-nav-hover': '#26221C',
    '--fs-nav-current-ring': '#3A342B',
    '--fs-nav-head': '#8F8676',
    '--fs-count-bg': '#2E2A24',
    '--fs-count-text': '#D8D0C2',
    '--fs-switch-off': '#4A443A',
    '--fs-switch-thumb': '#F3EEE3',
    '--fs-switch-thumb-on': '#15130F',
    '--fs-risk-tag-bg': '#3A1D15',
    '--fs-risk-tag-text': '#F0937A',
    '--fs-risk-line': '#5A2A1E',
    '--fs-risk-heading': '#F5A890',
    '--fs-danger-border': '#6B3022',
    '--fs-danger-hover': '#3A1D15',
    '--fs-enc-text': '#8DB4F0',
    '--fs-bar-track': '#2E2A24',
    '--fs-bar-fill': '#F3EEE3',
    '--fs-toast-bg': '#34302A',
    '--fs-toast-text': '#F3EEE3',
    '--fs-toast-border': '#5A5346',
    '--fs-toast-action': '#FFD666',
  },
};

export const chartSeries = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100'] as const;
export const chartOther = '#6E675A';

export const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {},
  light: {
    ...tokens.light,
    '--mantine-color-body': 'var(--fs-ground)',
    '--mantine-color-text': 'var(--fs-ink)',
    '--mantine-color-dimmed': 'var(--fs-sub)',
    '--mantine-color-anchor': 'var(--fs-link)',
    '--mantine-color-default': 'var(--fs-surface)',
    '--mantine-color-default-hover': 'var(--fs-hover)',
    '--mantine-color-default-color': 'var(--fs-ink)',
    '--mantine-color-default-border': 'var(--fs-control-border)',
  },
  dark: {
    ...tokens.dark,
    '--mantine-color-body': 'var(--fs-ground)',
    '--mantine-color-text': 'var(--fs-ink)',
    '--mantine-color-dimmed': 'var(--fs-sub)',
    '--mantine-color-anchor': 'var(--fs-link)',
    '--mantine-color-default': 'var(--fs-surface)',
    '--mantine-color-default-hover': 'var(--fs-hover)',
    '--mantine-color-default-color': 'var(--fs-ink)',
    '--mantine-color-default-border': 'var(--fs-control-border)',
  },
});

// Shade 6 is the filled button, shade 7 its hover.
const honey: MantineColorsTuple = [
  '#FFF8E1',
  '#FFEFC2',
  '#FFE499',
  '#FFD666',
  '#FFCB3D',
  '#FFC21F',
  '#FFB800',
  '#F2AE00',
  '#E0A200',
  '#C78F00',
];

const link: MantineColorsTuple = [
  '#E8EFFA',
  '#D2DFF4',
  '#A8C2E9',
  '#7BA3DD',
  '#5689D3',
  '#3E78CD',
  '#2B5BA8',
  '#24508F',
  '#1C3F78',
  '#143060',
];

// Warm dark palette: index 0 is text, 2 dimmed, 4 borders, 5 hover, 6 surfaces, 7 ground.
const dark: MantineColorsTuple = [
  '#F3EEE3',
  '#D8D0C2',
  '#A89F8F',
  '#7A7263',
  '#332E26',
  '#272320',
  '#1E1B16',
  '#15130F',
  '#100E0B',
  '#0B0A08',
];

export const theme = createTheme({
  colors: {
    honey,
    link,
    dark,
    // Kept for the public pages, which carry their own palette.
    'brand-primary': colorsTuple('#FFB800'),
    'brand-secondary': colorsTuple('#004E8F'),
    'brand-accent': colorsTuple('#00A3AA'),
    'neutral-dark': colorsTuple('#2E2E2E'),
    primary: link,
  },
  primaryColor: 'honey',
  primaryShade: 6,
  fontFamily: "'IBM Plex Sans', system-ui, -apple-system, 'Segoe UI', sans-serif",
  fontFamilyMonospace: "'IBM Plex Mono', ui-monospace, Menlo, monospace",
  fontSizes: {
    xs: '12px',
    sm: '13px',
    md: '14px',
    lg: '16px',
    xl: '18px',
  },
  lineHeights: {
    xs: '1.4',
    sm: '1.45',
    md: '1.5',
    lg: '1.5',
    xl: '1.5',
  },
  headings: {
    fontFamily: "'IBM Plex Sans', system-ui, -apple-system, 'Segoe UI', sans-serif",
    fontWeight: '600',
    sizes: {
      h1: { fontSize: '28px', lineHeight: '1.2' },
      h2: { fontSize: '16px', lineHeight: '1.35' },
      h3: { fontSize: '14px', lineHeight: '1.4' },
    },
  },
  radius: {
    xs: '4px',
    sm: '6px',
    md: '8px',
    lg: '12px',
    xl: '16px',
  },
  defaultRadius: 'sm',
  cursorType: 'pointer',
  components: {
    Button: Button.extend({
      defaultProps: { variant: 'default' },
      classNames: { root: classes.button },
    }),
    Switch: Switch.extend({
      defaultProps: { withThumbIndicator: false },
      classNames: { input: classes.switchInput, track: classes.switchTrack },
      vars: () => ({
        root: {
          '--switch-height': '24px',
          '--switch-width': '40px',
          '--switch-thumb-size': '18px',
          '--switch-track-label-padding': '3px',
          '--switch-radius': '12px',
          '--switch-color': 'var(--fs-switch-on)',
        },
      }),
    }),
    Checkbox: Checkbox.extend({
      vars: () => ({
        root: {
          '--checkbox-color': 'var(--fs-switch-on)',
          '--checkbox-icon-color': 'var(--fs-switch-thumb-on)',
        },
      }),
    }),
    Radio: Radio.extend({
      vars: () => ({
        root: {
          '--radio-color': 'var(--fs-switch-on)',
          '--radio-icon-color': 'var(--fs-switch-thumb-on)',
        },
      }),
    }),
  },
});

/** The app theme with every dialog's close button named in the current language. */
export function themeWithCloseLabel(closeLabel: string) {
  const closeButtonProps = { 'aria-label': closeLabel };
  return mergeThemeOverrides(
    theme,
    createTheme({
      components: {
        Modal: Modal.extend({ defaultProps: { closeButtonProps } }),
        Drawer: Drawer.extend({ defaultProps: { closeButtonProps } }),
      },
    })
  );
}
